import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MaintenanceView from 'src/features/maintenance/MaintenanceView';

import { axiosInstanceNew } from 'src/utils/axios';

// ----------------------------------------------------------------------

// Paths that stay reachable while maintenance is ON, so an admin can
// log in and turn it back off. Everything else shows the maintenance page.
const ALLOWED_PREFIXES = ['/admin'];

// Hostnames that always bypass maintenance (e.g. the develop preview deploy).
// The site renders normally on these even when maintenance is enabled.
const BYPASS_HOSTNAMES = ['develop--safiswap.netlify.app'];

function isAllowedPath(pathname) {
  return ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function isBypassHost() {
  if (typeof window === 'undefined') return false;
  return BYPASS_HOSTNAMES.includes(window.location.hostname);
}

export default function MaintenanceGate({ children }) {
  const { pathname } = useLocation();

  const [checked, setChecked] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let active = true;

    async function check() {
      // Skip the status check entirely on bypass hosts — the gate never
      // shows maintenance there, so there is nothing to wait for.
      if (isBypassHost()) {
        setOffline(false);
        setChecked(true);
        return;
      }

      try {
        const { data } = await axiosInstanceNew.get('/maintenance/status');

        if (active) {
          setOffline(Boolean(data?.enabled));
        }
      } catch (err) {
        // Fail open — a flaky status check shouldn't lock out real users
        if (active) {
          setOffline(false);
        }
      } finally {
        if (active) {
          setChecked(true);
        }
      }
    }

    check();

    return () => {
      active = false;
    };
  }, []);

  if (!checked) {
    return null; // or a splash/loader
  }

  if (offline && !isAllowedPath(pathname) && !isBypassHost()) {
    return <MaintenanceView />;
  }

  return children;
}

MaintenanceGate.propTypes = {
  children: PropTypes.node,
};
